import { ISetting } from '../../api/settings/interface/ISetting';
import { settingsApi } from '../../api/settings/settingsApi';
import { isCurrentUserDispatcher } from '../../utils/functions';
import { ITrailerSize } from '../../types/trailerSize';
import {
    BROKER_FEE_AMOUNT,
    BROKER_FEE_TYPE,
    CITY,
    COMPANY_ADDRESS,
    COMPANY_LOGO,
    COMPANY_NAME,
    DISPATCH_SUPPORT_NUMBER,
    EMAIL_ADDRESS,
    FAX_NUMBER,
    PHONE_NUMBER,
    SETTLEMENT_REPORT_BATCH_NUMBER,
    STATE,
    ZIP_CODE,
    IS_GREETING_MESSAGE_INCLUDED,
    IS_ORDER_NUMBER_INCLUDED,
    IS_DATE_INCLUDED,
    IS_TIME_FORMAT_12_HR,
    IS_PIN_DROP_NAME_INCLUDED,
    IS_LOCATION_INSTRUCTION_INCLUDED,
    IS_ESTIMATED_WEIGHT_INCLUDED,
    IS_ESTIMATED_HEAD_COUNT_INCLUDED,
    IS_COMMODITY_INCLUDED,
    IS_DRIVER_INSTRUCTION_INCLUDED,
    IS_ENDING_MESSAGE_INCLUDED,
    ENDING_MESSAGE,
    GREETING_MESSAGE,
    IS_SEND_INVOICE,
    INVOICE_RECIPIENTS,
    IS_QUICKBOOKS_CONNECTED,
    PASSWORD_EXPIRATION_MONTH,
    IS_BOL_REQUIRED_IN_MOBILE_APP,
    IS_ROUND_INVOICE_TOTALS,
    SHOW_HEADCOUNT_OR_QUANTITY,
    IS_QUICKBOOKS_ONLINE_CONNECTED,
    DEFAULT_GEOFENCE_RADIUS_MILES_SETTING,
    IS_TRAILER_49K_ENABLED,
    IS_TRAILER_52K_ENABLED,
    IS_TRAILER_55_62K_ENABLED,
    IS_TRAILER_68K_ENABLED,
    IS_TRAILER_10500_GAS_ENABLED,
    IS_TRAILER_9800_DIESEL_ENABLED,
    IS_TRAILER_9800_GAS_ENABLED,
    IS_TRAILER_9300_DF_ENABLED,
    TRAILER_SIZE_49K,
    TRAILER_SIZE_52K,
    TRAILER_SIZE_55_62K,
    TRAILER_SIZE_68K,
    TRAILER_SIZE_10500_GAS,
    TRAILER_SIZE_9800_DIESEL,
    TRAILER_SIZE_9800_GAS,
    TRAILER_SIZE_9300_DF,
} from './constants';
import {
    ICompanyDetails,
    ICommunicationDetails,
    IInvoicingDetails,
} from './interfaces/ISettingsDetails';
import { dispatchManagerFeatures } from '../../featureFlags/dispatchManagerFeatures';

export interface ISettingUtils {
    getCompanyDetails: (settings: ISetting[]) => ICompanyDetails;
    getCommunicationDetails: (settings: ISetting[]) => ICommunicationDetails;
    getInvoicingDetails: (settings: ISetting[]) => IInvoicingDetails;
    getQBUsers: () => Promise<boolean>;
    getEnabledTrailerSizes: () => string[];
    getTrailerSizes: () => ITrailerSize[];
}

export class SettingUtils implements ISettingUtils {
    private settings: ISetting[];

    constructor(settings: ISetting[]) {
        this.settings = settings;
    }

    private getCompanyValue(companyName: string): string {
        const setting = this.settings.find(
            setting => setting.name === companyName,
        );
        return setting?.value !== null ? String(setting?.value) : '';
    }

    private getBooleanSettingValue(settingName: string): boolean {
        const setting = this.settings.find(
            setting => setting.name === settingName,
        );
        return setting?.value === true;
    }

    private getNumericSettingValue(settingName: string): number | undefined {
        const setting = this.settings.find(
            setting => setting.name === settingName,
        );

        if (setting?.value === null || setting?.value === undefined) {
            return undefined;
        }

        const numericValue = Number(setting.value);

        return Number.isNaN(numericValue) ? undefined : numericValue;
    }

    getPasswordExpiryMonths = (): number => {
        const passwordExpiryMonths = this.getCompanyValue(
            PASSWORD_EXPIRATION_MONTH,
        );

        return Number(passwordExpiryMonths);
    };

    getIsBolRequiredInMobileApp = (): boolean => {
        const isBolRequiredInMobileApp = this.getBooleanSettingValue(
            IS_BOL_REQUIRED_IN_MOBILE_APP,
        );
        return isBolRequiredInMobileApp;
    };

    getShowHeadCountOrQuantity = (): string => {
        const showHeadCountOrQuantity = this.getCompanyValue(
            SHOW_HEADCOUNT_OR_QUANTITY,
        );
        return showHeadCountOrQuantity;
    };

    //   DEPRECATED: Legacy method - use getTrailerSizes() instead
    getEnabledTrailerSizes = (): string[] => {
        interface TrailerSizeMapping {
            settingName: string;
            code: string;
        }

        const trailerSizeMapping: TrailerSizeMapping[] = [
            { settingName: IS_TRAILER_49K_ENABLED, code: TRAILER_SIZE_49K },
            { settingName: IS_TRAILER_52K_ENABLED, code: TRAILER_SIZE_52K },
            {
                settingName: IS_TRAILER_55_62K_ENABLED,
                code: TRAILER_SIZE_55_62K,
            },
            { settingName: IS_TRAILER_68K_ENABLED, code: TRAILER_SIZE_68K },
            {
                settingName: IS_TRAILER_10500_GAS_ENABLED,
                code: TRAILER_SIZE_10500_GAS,
            },
            {
                settingName: IS_TRAILER_9800_DIESEL_ENABLED,
                code: TRAILER_SIZE_9800_DIESEL,
            },
            {
                settingName: IS_TRAILER_9800_GAS_ENABLED,
                code: TRAILER_SIZE_9800_GAS,
            },
            {
                settingName: IS_TRAILER_9300_DF_ENABLED,
                code: TRAILER_SIZE_9300_DF,
            },
        ];

        return trailerSizeMapping
            .filter(({ settingName }) =>
                this.getBooleanSettingValue(settingName),
            )
            .map(({ code }) => code);
    };
    getTrailerSizes = (): ITrailerSize[] => {
        const { isTrailerSizePreferencesEnabled } = dispatchManagerFeatures;

        if (!isTrailerSizePreferencesEnabled) {
            return this.getLegacyTrailerSizes();
        }

        const trailerSizeSetting = this.settings.find(
            setting => setting.name === 'TrailerSizes',
        );

        if (
            trailerSizeSetting?.value &&
            Array.isArray(trailerSizeSetting.value)
        ) {
            return trailerSizeSetting.value as unknown as ITrailerSize[];
        }

        return this.getLegacyTrailerSizes();
    };

    private getLegacyTrailerSizes = (): ITrailerSize[] => {
        interface LegacyMapping {
            settingName: string;
            name: string;
            displayName: string;
            category: string;
            subCategory?: string;
        }

        const legacyMapping: LegacyMapping[] = [
            {
                settingName: IS_TRAILER_49K_ENABLED,
                name: TRAILER_SIZE_49K,
                displayName: '49k',
                category: 'Livestock',
            },
            {
                settingName: IS_TRAILER_52K_ENABLED,
                name: TRAILER_SIZE_52K,
                displayName: '52k',
                category: 'Livestock',
            },
            {
                settingName: IS_TRAILER_55_62K_ENABLED,
                name: TRAILER_SIZE_55_62K,
                displayName: '55-62k',
                category: 'Livestock',
            },
            {
                settingName: IS_TRAILER_68K_ENABLED,
                name: TRAILER_SIZE_68K,
                displayName: '68k',
                category: 'Livestock',
            },
            {
                settingName: IS_TRAILER_10500_GAS_ENABLED,
                name: TRAILER_SIZE_10500_GAS,
                displayName: '10,500 Gas',
                category: 'Fuel',
                subCategory: 'SuperTubeSemi',
            },
            {
                settingName: IS_TRAILER_9800_DIESEL_ENABLED,
                name: TRAILER_SIZE_9800_DIESEL,
                displayName: '9,800 Diesel',
                category: 'Fuel',
                subCategory: 'SuperTubeSemi',
            },
            {
                settingName: IS_TRAILER_9800_GAS_ENABLED,
                name: TRAILER_SIZE_9800_GAS,
                displayName: '9,800 Gas',
                category: 'Fuel',
                subCategory: 'TruckTrailer',
            },
            {
                settingName: IS_TRAILER_9300_DF_ENABLED,
                name: TRAILER_SIZE_9300_DF,
                displayName: '9,300 DF',
                category: 'Fuel',
                subCategory: 'TruckTrailer',
            },
        ];

        return legacyMapping
            .filter(({ settingName }) =>
                this.getBooleanSettingValue(settingName),
            )
            .map(({ name, displayName, category, subCategory }, index) => ({
                trailerSizeId: index + 1,
                name,
                displayName,
                category,
                subCategory,
                isActive: true,
                isDefault: index === 0,
            }));
    };

    getCompanyDetails = (): ICompanyDetails => {
        const companyLogoSetting = this.settings.find(
            setting => setting.name === COMPANY_LOGO,
        );
        const brokerFeeType = this.getCompanyValue(BROKER_FEE_TYPE);
        const brokerFeeAmount = this.getCompanyValue(BROKER_FEE_AMOUNT);
        const companyName = this.getCompanyValue(COMPANY_NAME);
        const companyAddress = this.getCompanyValue(COMPANY_ADDRESS);
        const city = this.getCompanyValue(CITY);
        const state = this.getCompanyValue(STATE);
        const zipCode = this.getCompanyValue(ZIP_CODE);
        const phoneNumber = this.getCompanyValue(PHONE_NUMBER);
        const dispatchSupportNumber = this.getCompanyValue(
            DISPATCH_SUPPORT_NUMBER,
        );
        const faxNumber = this.getCompanyValue(FAX_NUMBER);
        const emailAddress = this.getCompanyValue(EMAIL_ADDRESS);
        const companyLogo = this.getCompanyValue(COMPANY_LOGO);
        const baseSettingId = companyLogoSetting?.baseSettingId;
        const settlementReportBatchNumber = this.getCompanyValue(
            SETTLEMENT_REPORT_BATCH_NUMBER,
        );
        const defaultGeofenceRadius = this.getNumericSettingValue(
            DEFAULT_GEOFENCE_RADIUS_MILES_SETTING,
        );

        return {
            brokerFeeType,
            brokerFeeAmount,
            companyName,
            emailAddress,
            phoneNumber,
            faxNumber,
            dispatchSupportNumber,
            companyAddress,
            city,
            state,
            zipCode,
            companyLogo,
            baseSettingId,
            settlementReportBatchNumber,
            defaultGeofenceRadius,
        };
    };

    getCommunicationDetails = (): ICommunicationDetails => {
        const greetingMessage = this.getCompanyValue(GREETING_MESSAGE);
        const endingMessage = this.getCompanyValue(ENDING_MESSAGE);
        const isGreetingMessageIncluded = this.getBooleanSettingValue(
            IS_GREETING_MESSAGE_INCLUDED,
        );
        const isEndingMessageIncluded = this.getBooleanSettingValue(
            IS_ENDING_MESSAGE_INCLUDED,
        );
        const isDriverInstructionIncluded = this.getBooleanSettingValue(
            IS_DRIVER_INSTRUCTION_INCLUDED,
        );
        const isOrderNumberIncluded = this.getBooleanSettingValue(
            IS_ORDER_NUMBER_INCLUDED,
        );
        const isDateIncluded = this.getBooleanSettingValue(IS_DATE_INCLUDED);
        const isTimeFormat12Hr =
            this.getBooleanSettingValue(IS_TIME_FORMAT_12_HR);
        const isPinDropNameIncluded = this.getBooleanSettingValue(
            IS_PIN_DROP_NAME_INCLUDED,
        );
        const isLocationInstructionIncluded = this.getBooleanSettingValue(
            IS_LOCATION_INSTRUCTION_INCLUDED,
        );
        const isEstimatedWeightIncluded = this.getBooleanSettingValue(
            IS_ESTIMATED_WEIGHT_INCLUDED,
        );
        const isEstimatedHeadCountIncluded = this.getBooleanSettingValue(
            IS_ESTIMATED_HEAD_COUNT_INCLUDED,
        );
        const isCommodityIncluded = this.getBooleanSettingValue(
            IS_COMMODITY_INCLUDED,
        );

        return {
            greetingMessage,
            endingMessage,
            isGreetingMessageIncluded,
            isEndingMessageIncluded,
            isDriverInstructionIncluded,
            isOrderNumberIncluded,
            isDateIncluded,
            isTimeFormat12Hr,
            isPinDropNameIncluded,
            isLocationInstructionIncluded,
            isEstimatedWeightIncluded,
            isEstimatedHeadCountIncluded,
            isCommodityIncluded,
        };
    };

    getInvoicingDetails = (): IInvoicingDetails => {
        const getRecipientsValue = (companyName: string): string[] => {
            const setting = this.settings.find(
                setting => setting.name === companyName,
            );

            if (setting !== undefined) {
                return setting?.value as string[];
            }
            return [];
        };

        const isSendInvoices = this.getBooleanSettingValue(IS_SEND_INVOICE);
        const invoiceRecipients = getRecipientsValue(INVOICE_RECIPIENTS);
        const isQuickBooksConnected = this.getBooleanSettingValue(
            IS_QUICKBOOKS_CONNECTED,
        );
        const isRoundInvoiceTotals = this.getBooleanSettingValue(
            IS_ROUND_INVOICE_TOTALS,
        );

        const isQuickBooksOnlineConnected = this.getBooleanSettingValue(
            IS_QUICKBOOKS_ONLINE_CONNECTED,
        );
        return {
            isSendInvoices,
            invoiceRecipients,
            isQuickBooksConnected,
            isRoundInvoiceTotals,
            isQuickBooksOnlineConnected,
        };
    };

    getQBUsers = async (): Promise<boolean> => {
        if (isCurrentUserDispatcher()) {
            return false;
        }
        const qbUserList = await settingsApi.fetchQBUsersList();
        return qbUserList.length > 0;
    };

    async fetchTenantSettings(): Promise<ICompanyDetails | undefined> {
        try {
            const companyDetail = this.getCompanyDetails();

            if (companyDetail.companyLogo) {
                const companyLogoDetails = companyDetail.companyLogo;
                const formattedCompanyLogoDetails =
                    JSON.parse(companyLogoDetails);
                const attachmentId = formattedCompanyLogoDetails.AttachmentId;
                const accessToken = formattedCompanyLogoDetails.AccessToken;

                const attachmentResponse =
                    await settingsApi.fetchCompanyProfilePicture(
                        companyDetail.baseSettingId || 0,
                        accessToken,
                        attachmentId,
                    );

                const logoUrl = URL.createObjectURL(attachmentResponse.data);
                if (companyDetail.companyLogo !== logoUrl) {
                    companyDetail.companyLogo = logoUrl;
                }
                return companyDetail;
            } else {
                return companyDetail;
            }
        } catch {
            return undefined;
        }
    }
}
